import { ComponentProps, useEffect, useRef, useState } from "react";
import { SkillTag } from "../../client";
import EditorSkillTagButton from "../../components/atoms/EditorSkillTagButton";
import {
  DynamicInput,
  DynamicInputRef,
} from "../../components/input/DynamicInput";
import { WorkplaceHeader } from "../dashboard/home/components/link-creator/WorkplaceHeader";
import { cn } from "../../components/utils/cn";
import {
  isString,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faCirclePlus,
  faClose,
  faGear,
  faRemove,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { faEdit } from "@fortawesome/free-regular-svg-icons";

interface Props extends ComponentProps<typeof WorkplaceHeader> {
  id: string | undefined;
  description?: string;
  skillTags: SkillTag[];
  showSkills?: boolean;
  showDescription?: boolean;
  pdfMode?: boolean;
  isEditable: boolean;
  onWorkplaceEdit: (description: string) => void;
  onWorkplaceContentUpdate?: (
    id: string | undefined,
    role: string,
    description: string | undefined,
    name: string | undefined,
    startDate: string | undefined,
    endDate: string | undefined,
  ) => void;
  onProjectClickAction?: (action: string) => void;
  showSkillIcons?: boolean;
  avoidPageBreak?: boolean;
  /**
   * Enables inline editing of the text fields only — role/name (header) and
   * description — without the full edit menu, add-skill, or date editing. Used by
   * the template editor; independent of `isEditable` (the full master-edit mode).
   */
  fieldsEditable?: boolean;
}

const ProfileWorkplace = ({
  id,
  description,
  skillTags,
  showSkills,
  showDescription,
  pdfMode,
  isEditable,
  onWorkplaceEdit,
  onWorkplaceContentUpdate,
  onProjectClickAction,
  showSkillIcons,
  avoidPageBreak,
  fieldsEditable,
  ...props
}: Props) => {
  const textEditable = isEditable || !!fieldsEditable;
  const inputRef = useRef<DynamicInputRef>(null);
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the click is on a button or within the actions menu
      const isButton = target.closest("button");
      const isWithinActions = actionsRef.current?.contains(target);

      if (showActions && !isButton && !isWithinActions) {
        setShowActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  return (
    <div className="group relative flex flex-row">
      <div className="absolute left-0 flex flex-col">
        {isEditable && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 pt-1">
            <button
              onClick={() => setShowActions(!showActions)}
              className="cursor-pointer"
            >
              <FontAwesomeIcon
                icon={faEdit}
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  showActions && "rotate-180",
                )}
              />
            </button>
          </div>
        )}

        {showActions && (
          <div
            ref={actionsRef}
            className="flex flex-col gap-2 bg-white z-[9999] rounded-lg shadow-lg p-1"
            onMouseLeave={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon
                icon={faClose}
                className="w-4 h-4 text-gray-600"
              />
              <span className="text-sm text-gray-700">Close</span>
            </button>
            <button
              className={cn(
                "flex flex-row items-center gap-2 rounded-2xl px-4 py-5 text-sm transition-all hover:bg-gray-1 active:scale-95",
              )}
              onClick={() => {
                if (onProjectClickAction) {
                  onProjectClickAction("remove");
                }
                setShowActions(false);
              }}
            >
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-red-3" />
              <span className="text-sm text-gray-700">Delete workplace</span>
            </button>
          </div>
        )}
      </div>

      <div
        className={cn(
          "flex-grow transition-transform duration-300",
          isEditable && "group-hover:translate-x-6",
        )}
      >
        <div style={avoidPageBreak ? { breakInside: "avoid", pageBreakInside: "avoid", breakAfter: "avoid", pageBreakAfter: "avoid" } : undefined}>
          <WorkplaceHeader
            {...props}
            isEditable={textEditable}
            datesEditable={isEditable}
            onHeaderContentUpdate={(role, name, startDate, endDate) => {
              if (onWorkplaceContentUpdate)
                onWorkplaceContentUpdate(
                  id,
                  role,
                  description,
                  name,
                  startDate,
                  endDate,
                );
            }}
          />
        </div>
        <div className="relative">
          {textEditable ? (
            <DynamicInput
              className={cn(
                "text-sm whitespace-pre-line",
                "transition-all duration-300 overflow-hidden",
                showDescription ? "opacity-100" : "max-h-0 opacity-0 pt-0",
              )}
              inputClassName="text-sm"
              disabled={!textEditable}
              label="Describe your role and responsibilities"
              validate={isString}
              value={description}
              ref={inputRef}
              rows={1}
              editable={textEditable}
              onBlur={(
                event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
              ) => {
                const inputValue = event.target.value;
                onWorkplaceEdit(inputValue);
              }}
            />
          ) : (
            <>
              {showDescription ? (
                <div className="text-sm whitespace-pre-line mt-2">
                  {description}
                </div>
              ) : null}
            </>
          )}
        </div>
        <div
          style={{ pageBreakInside: "avoid" }}
          className={cn(
            "flex flex-row flex-wrap overflow-y-auto max-w-full overflow-visible",
            showSkills && (skillTags?.length > 0 || isEditable)
              ? "transition-all duration-300 min-h-[3rem] max-h-none opacity-100"
              : "max-h-0 opacity-0 scale-y-0 pt-0",
            showDescription && "pt-1",
          )}
        >
          {isEditable && (
            <div
              className="group/addskill flex flex-row justify-center items-center text-sm gap-1 transform transition-transform hover:scale-110 mr-1 cursor-pointer"
              onClick={() =>
                onProjectClickAction && onProjectClickAction("tags")
              }
            >
              <FontAwesomeIcon
                icon={faCirclePlus}
                className="w-4 h-4 group-hover/addskill:scale-125 transition-transform duration-200"
              />
              <label className="text-xs cursor-pointer">Add skill</label>
            </div>
          )}
          {skillTags?.map((skillTag) => (
            <div key={skillTag.name} className="flex flex-row">
              <EditorSkillTagButton
                profileSkillTag={skillTag}
                text={skillTag.name}
                disableEditing={true}
                handleClick={() =>
                  onProjectClickAction && onProjectClickAction("tags")
                }
                keepLeftPadding={true}
                isStatic={true}
                displayInfoOnHover={true}
                pdfMode={pdfMode}
                hideIcon={!showSkillIcons}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileWorkplace;
